import jsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';
import { formatMoney } from './formatters';

/**
 * Generate swing tags PDF
 * Specifications:
 * - Letter size paper (8.5" x 11" = 215.9mm x 279.4mm)
 * - Each tag: 70mm x 40mm
 * - 3 tags per row
 * - Information: name, size, color, date, price
 */

export const generateSwingTagsPDF = async (item) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  // Updated Tag dimensions
  const tagWidth = 98; // 70 * 1.4
  const tagHeight = 40;
  const tagsPerRow = 2;
  const marginLeft = 9.95; // Updated centering for 2 tags
  const marginTop = 10;
  const gapX = 0; 
  const gapY = 5; 

  const variations = item.variations || [];
  const tags = [];

  variations.forEach(variation => {
    for (let i = 0; i < variation.quantity; i++) {
      tags.push({
        name: item.name || variation.name,
        brand: item.brand || variation.brand || '',
        color: variation.color,
        size: variation.size,
        price: formatMoney(variation.price),
        sku: variation.sku,
        date: new Date().toLocaleDateString()
      });
    }
  });

  if (tags.length === 0) throw new Error('No tags to generate');

  tags.forEach((tag, index) => {
    // 14 tags per page (7 rows * 2)
    const row = Math.floor((index % 14) / tagsPerRow);
    const col = index % tagsPerRow;
    
    if (index > 0 && index % 14 === 0) {
      pdf.addPage();
    }

    const x = marginLeft + col * (tagWidth + gapX);
    const y = marginTop + row * (tagHeight + gapY);

    drawSwingTag(pdf, x, y, tagWidth, tagHeight, tag);
  });

  const fileName = `swing-tags-${item.name.replace(/\s+/g, '-')}-${Date.now()}.pdf`;
  pdf.save(fileName);
};

const drawSwingTag = (pdf, x, y, width, height, tag) => {
  // 1. CROP MARKS (Light gray guides)
  pdf.setDrawColor(200); 
  pdf.setLineWidth(0.1);
  const s = 3; 
  pdf.line(x, y, x + s, y); pdf.line(x, y, x, y + s);
  pdf.line(x + width, y, x + width - s, y); pdf.line(x + width, y, x + width, y + s);
  pdf.line(x, y + height, x + s, y + height); pdf.line(x, y + height, x, y + height - s);
  pdf.line(x + width, y + height, x + width - s, y + height); pdf.line(x + width, y + height, x + width, y + height - s);

  // 2. BRAND NAME
  pdf.setTextColor(100); // Dark gray for the brand
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text(tag.brand || 'mrich.ca', x + 5, y + 5);

  // 3. ITEM INFO (Shifted down slightly to accommodate brand)
  pdf.setTextColor(0); // Back to black
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  const nameLines = pdf.splitTextToSize(tag.name, width / 2);
  pdf.text(nameLines, x + 5, y + 12); // Moved from y+8 to y+12

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const infoY = y + 12 + (nameLines.length * 5);
  pdf.text(`Color: ${tag.color}`, x + 5, infoY);
  pdf.text(`Size: ${tag.size}`, x + 5, infoY + 5);

  /*
  // 4. BARCODE & SKU
  const barcodeWidth = 45;
  const barcodeHeight = 10;
  const barcodeX = x + (width - barcodeWidth) / 2;
  const barcodeY = y + height - 18;

  if (tag.sku) {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, tag.sku, { format: 'CODE128', width: 1, height: 30, displayValue: false });
    const barcodeImage = canvas.toDataURL('image/png');
    pdf.addImage(barcodeImage, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);
    
    pdf.setFontSize(6);
    pdf.text(tag.sku, barcodeX + barcodeWidth / 2, barcodeY + barcodeHeight + 3, { align: 'center' });
  }*/

  // 4. UPDATED BARCODE LOGIC
  const barcodeWidth = 60; // Increased from 45 to give it more horizontal room
  const barcodeHeight = 12; // Increased slightly for better vertical scanning
  const barcodeX = x + (width - barcodeWidth) / 2;
  const barcodeY = y + height - 20;

  if (tag.sku) {
    try {
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, tag.sku, {
        format: 'CODE128',
        width: 2,           // INCREASED: Makes the bars thicker and easier to scan
        height: 40,          // INCREASED: Standard height for better sensor pickup
        displayValue: false,
        margin: 10           // ADDED: Creates a "Quiet Zone" so the scanner knows where the barcode starts
      });
      
      const barcodeImage = canvas.toDataURL('image/png');
      // We use 'FAST' compression to keep the bars sharp
      pdf.addImage(barcodeImage, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight, undefined, 'FAST');
      
      pdf.setFontSize(7);
      pdf.text(tag.sku, barcodeX + barcodeWidth / 2, barcodeY + barcodeHeight + 4, { align: 'center' });
    } catch (error) {
      console.error('Barcode error:', error);
    }
  }

  // 5. PRICE & DATE (Standardized to the right side)
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  const priceText = `${tag.price}`;
  pdf.text(priceText, barcodeX + barcodeWidth, barcodeY - 2, { align: 'right' });

  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');
  pdf.text(tag.date, x + width - 5, y + height - 3, { align: 'right' });
  
  // Scissors Icon
  pdf.setFontSize(8);
  pdf.text('✂', x + width - 5, y + 3);
};

/*
const drawSwingTag = (pdf, x, y, width, height, tag) => {
  // Scissors Icon (Now acts as the only cut guide)
  pdf.setDrawColor(0);
  pdf.setFontSize(8);
  pdf.text('✂', x + width - 5, y + 3);

  // Item Information (Left Aligned)
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  const nameLines = pdf.splitTextToSize(tag.name, width / 2);
  pdf.text(nameLines, x + 5, y + 8);

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const infoY = y + 8 + (nameLines.length * 5);
  pdf.text(`Color: ${tag.color}`, x + 5, infoY);
  pdf.text(`Size: ${tag.size}`, x + 5, infoY + 5);

  // Barcode Logic
  const barcodeWidth = 45;
  const barcodeHeight = 10;
  const barcodeX = x + (width - barcodeWidth) / 2;
  const barcodeY = y + height - 18;

  if (tag.sku) {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, tag.sku, { format: 'CODE128', width: 1, height: 30, displayValue: false });
    const barcodeImage = canvas.toDataURL('image/png');
    pdf.addImage(barcodeImage, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);
    
    pdf.setFontSize(6);
    pdf.text(tag.sku, barcodeX + barcodeWidth / 2, barcodeY + barcodeHeight + 3, { align: 'center' });
  }

  // Price (Right Aligned above Barcode)
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  const priceText = `$${tag.price.toFixed(2)}`;
  pdf.text(priceText, barcodeX + barcodeWidth, barcodeY - 2, { align: 'right' });

  // Date (Bottom Right)
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');
  pdf.text(tag.date, x + width - 5, y + height - 3, { align: 'right' });
};


const drawSwingTag = (pdf, x, y, width, height, tag) => {
  // Border & Cut Indicator
  pdf.setDrawColor(0);
  pdf.setLineWidth(0.3);
  pdf.rect(x, y, width, height);
  pdf.setFontSize(8);
  pdf.text('✂', x + width - 5, y + 3);

  // Item Information (Left Aligned)
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  const nameLines = pdf.splitTextToSize(tag.name, width / 2); // Use half width for text
  pdf.text(nameLines, x + 5, y + 8);

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const infoY = y + 8 + (nameLines.length * 5);
  pdf.text(`Color: ${tag.color}`, x + 5, infoY);
  pdf.text(`Size: ${tag.size}`, x + 5, infoY + 5);

  // Barcode (Centered horizontally)
  const barcodeWidth = 45; // Slightly wider barcode for the wider tag
  const barcodeHeight = 10;
  const barcodeX = x + (width - barcodeWidth) / 2;
  const barcodeY = y + height - 18;

  if (tag.sku) {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, tag.sku, { format: 'CODE128', width: 1, height: 30, displayValue: false });
    const barcodeImage = canvas.toDataURL('image/png');
    pdf.addImage(barcodeImage, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);
    
    pdf.setFontSize(6);
    pdf.text(tag.sku, barcodeX + barcodeWidth / 2, barcodeY + barcodeHeight + 3, { align: 'center' });
  }

  // Price (Right Aligned above Barcode)
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  const priceText = `$${tag.price.toFixed(2)}`;
  // Anchored to the right edge of the barcode
  pdf.text(priceText, barcodeX + barcodeWidth, barcodeY - 2, { align: 'right' });

  // Date (Bottom Right)
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');
  pdf.text(tag.date, x + width - 5, y + height - 3, { align: 'right' });
};

export const generateSwingTagsPDF = async (item) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  // Tag dimensions
  const tagWidth = 70;
  const tagHeight = 40;
  const tagsPerRow = 3;
  const marginLeft = 7.95; // Center 3 tags (215.9 - 3*70) / 2
  const marginTop = 10;
  const gapX = 0; // No horizontal gap between tags
  const gapY = 5; // 5mm vertical gap

  // Collect all variations to print
  const variations = item.variations || [];
  const tags = [];

  variations.forEach(variation => {
    for (let i = 0; i < variation.quantity; i++) {
      tags.push({
        name: item.name,
        color: variation.color,
        size: variation.size,
        price: variation.price,
        sku: variation.sku,
        date: new Date().toLocaleDateString()
      });
    }
  });

  if (tags.length === 0) {
    throw new Error('No tags to generate');
  }

  let currentPage = 0;
  
  tags.forEach((tag, index) => {
    const row = Math.floor((index % 21) / tagsPerRow); // 21 tags per page (7 rows * 3)
    const col = index % tagsPerRow;
    
    // Add new page if needed
    if (index > 0 && index % 21 === 0) {
      pdf.addPage();
      currentPage++;
    }

    const x = marginLeft + col * (tagWidth + gapX);
    const y = marginTop + row * (tagHeight + gapY);

    drawSwingTag(pdf, x, y, tagWidth, tagHeight, tag);
  });

  // Save PDF
  const fileName = `swing-tags-${item.name.replace(/\s+/g, '-')}-${Date.now()}.pdf`;
  pdf.save(fileName);
};
*/

/**
 * Draw a single swing tag
 */
/*
const drawSwingTag = (pdf, x, y, width, height, tag) => {
  // Border
  pdf.setDrawColor(0);
  pdf.setLineWidth(0.3);
  pdf.rect(x, y, width, height);

  // Add scissors icon at top (cut line indicator)
  pdf.setFontSize(8);
  pdf.text('✂', x + width - 5, y + 3);

  // Item name - bold and larger
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  const nameLines = pdf.splitTextToSize(tag.name, width - 10);
  pdf.text(nameLines, x + 5, y + 8);

  // Color and Size
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const colorSizeY = y + 8 + (nameLines.length * 5);
  pdf.text(`Color: ${tag.color}`, x + 5, colorSizeY);
  pdf.text(`Size: ${tag.size}`, x + 5, colorSizeY + 5);

  // Price - large and prominent
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`$${tag.price.toFixed(2)}`, x + 5, y + height - 10);

  // Date printed - small, bottom right
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');
  pdf.text(tag.date, x + width - 25, y + height - 3);

  // Barcode - center bottom
  if (tag.sku) {
    try {
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, tag.sku, {
        format: 'CODE128',
        width: 1,
        height: 30,
        displayValue: false,
        margin: 0
      });
      
      const barcodeImage = canvas.toDataURL('image/png');
      const barcodeWidth = 40;
      const barcodeHeight = 10;
      const barcodeX = x + (width - barcodeWidth) / 2;
      const barcodeY = y + height - 20;
      
      pdf.addImage(barcodeImage, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);
      
      // SKU text below barcode
      pdf.setFontSize(6);
      pdf.text(tag.sku, barcodeX + barcodeWidth / 2, barcodeY + barcodeHeight + 3, { align: 'center' });
    } catch (error) {
      console.error('Error generating barcode:', error);
      // Fallback: just show SKU text
      pdf.setFontSize(7);
      pdf.text(`SKU: ${tag.sku}`, x + width / 2, y + height - 15, { align: 'center' });
    }
  }
};


const drawSwingTag = (pdf, x, y, width, height, tag) => {
  // Border & Scissors (Existing logic)
  pdf.setDrawColor(0);
  pdf.setLineWidth(0.3);
  pdf.rect(x, y, width, height);
  pdf.setFontSize(8);
  pdf.text('✂', x + width - 5, y + 3);

  // Item name
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  const nameLines = pdf.splitTextToSize(tag.name, width - 10);
  pdf.text(nameLines, x + 5, y + 8);

  // Color and Size
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const colorSizeY = y + 8 + (nameLines.length * 5);
  pdf.text(`Color: ${tag.color}`, x + 5, colorSizeY);
  pdf.text(`Size: ${tag.size}`, x + 5, colorSizeY + 5);

  // --- REPOSITIONED ELEMENTS ---

  // 1. Barcode (Anchor point for the price)
  const barcodeWidth = 40;
  const barcodeHeight = 10;
  const barcodeX = x + (width - barcodeWidth) / 2;
  const barcodeY = y + height - 18; // Shifted down slightly to make room above

  if (tag.sku) {
    try {
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, tag.sku, {
        format: 'CODE128',
        width: 1,
        height: 30,
        displayValue: false,
        margin: 0
      });
      const barcodeImage = canvas.toDataURL('image/png');
      pdf.addImage(barcodeImage, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);
      
      // SKU text below barcode
      pdf.setFontSize(6);
      pdf.text(tag.sku, barcodeX + barcodeWidth / 2, barcodeY + barcodeHeight + 3, { align: 'center' });
    } catch (error) {
      console.error('Barcode error:', error);
    }
  }

  // 2. Price (Moved on top of the barcode, aligned to the right edge of barcode)
  pdf.setFontSize(14); // Slightly smaller to ensure it fits the "top" area better
  pdf.setFont('helvetica', 'bold');
  const priceText = `$${tag.price.toFixed(2)}`;
  // Align to the right edge of the barcode: (barcodeX + barcodeWidth)
  pdf.text(priceText, barcodeX + barcodeWidth, barcodeY - 2, { align: 'right' });

  // 3. Date (Kept at the very bottom right)
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');
  pdf.text(tag.date, x + width - 5, y + height - 3, { align: 'right' });
};
*/

/**
 * Generate Avery 5167 labels PDF
 * Specifications (Avery 5167 - Return Address Labels):
 * - Label size: 1/2" x 1-3/4" (12.7mm x 44.45mm)
 * - 80 labels per page (4 columns x 20 rows)
 * - Page margins: Left 7.62mm (0.3"), Top 13.1mm
 * - Label spacing: 7.62mm horizontal gutter (0.3"), 0mm vertical gap
 * - Safe zone: 1mm margin inside each label
 * - Content: barcode, price, size
 * @param {Object} item - Item data
 * @param {number} startRow - Starting row (1-20), default 1
 * @param {number} startCol - Starting column (1-4), default 1
 */
export const generateAveryLabelsPDF = async (item, startRow = 1, startCol = 1) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  // Avery 5167 specifications (verified dimensions)
  const labelWidth = 44.45;   // 1.75 inches = 44.45mm
  const labelHeight = 12.7;   // 0.5 inches = 12.7mm
  const labelsPerRow = 4;     // 4 columns
  const labelsPerCol = 20;    // 20 rows
  const labelsPerPage = labelsPerRow * labelsPerCol; // 80 labels per page

  // Avery 5167 page margins (from Avery template specifications)
  const marginLeft = 8.89;    // Left margin in mm (0.3") - 7.62
  const marginTop = 13.1;     // Top margin in mm

  // Spacing between labels
  const gapX = 7.62;          // Horizontal gutter between columns (0.3")
  const gapY = 0;             // No vertical gap

  const variations = item.variations || [];
  const labels = [];

  variations.forEach(variation => {
    for (let i = 0; i < variation.quantity; i++) {
      labels.push({
        name: item.name || variation.name,
        size: variation.size,
        price: formatMoney(variation.price),
        sku: variation.sku
      });
    }
  });

  if (labels.length === 0) throw new Error('No labels to generate');

  // Calculate starting position offset (convert to 0-indexed)
  const startRowIndex = startRow - 1;
  const startColIndex = startCol - 1;
  const startOffset = startRowIndex * labelsPerRow + startColIndex;

  labels.forEach((label, labelIndex) => {
    // Add offset to account for starting position
    const absoluteIndex = labelIndex + startOffset;
    const pageIndex = Math.floor(absoluteIndex / labelsPerPage);
    const indexOnPage = absoluteIndex % labelsPerPage;

    const row = Math.floor(indexOnPage / labelsPerRow);
    const col = indexOnPage % labelsPerRow;

    // Add new pages as needed
    while (pdf.internal.pages.length <= pageIndex + 1) {
      pdf.addPage();
    }

    // Set the correct page
    if (pageIndex > 0) {
      pdf.setPage(pageIndex + 1);
    }

    // Calculate position with proper margins and gaps
    const x = marginLeft + col * (labelWidth + gapX);
    const y = marginTop + row * (labelHeight + gapY);

    drawAveryLabel(pdf, x, y, labelWidth, labelHeight, label);
  });

  const fileName = `avery-labels-${item.name.replace(/\s+/g, '-')}-${Date.now()}.pdf`;
  pdf.save(fileName);
};

/**
 * Draw a single Avery 8927 label
 * Content: Barcode + number, Price
 * Respects 1mm safe zone on all sides
 */
const drawAveryLabel = (pdf, x, y, width, height, label) => {
  // Safe zone: 1mm margin inside label boundaries
  const safeZone = 1.0;
  const contentX = x + safeZone;
  const contentY = y + safeZone;
  const contentWidth = width - (2 * safeZone);
  const contentHeight = height - (2 * safeZone);

  // 1. BARCODE (top, centered)
  const barcodeWidth = contentWidth - 2; // Additional 1mm padding
  const barcodeHeight = 5.0;
  const barcodeX = contentX + 1;
  const barcodeY = contentY + 0.3;

  if (label.sku) {
    try {
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, label.sku, {
        format: 'CODE128',
        width: 1.3,
        height: 28,
        displayValue: false,
        margin: 0
      });

      const barcodeImage = canvas.toDataURL('image/png');
      pdf.addImage(barcodeImage, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight, undefined, 'FAST');

      // Barcode number below barcode
      pdf.setFontSize(4.5);
      pdf.setFont('helvetica', 'normal');
      pdf.text(label.sku, contentX + contentWidth / 2, barcodeY + barcodeHeight + 1.8, { align: 'center' });
    } catch (error) {
      console.error('Barcode error:', error);
    }
  }

  // 2. PRICE (bottom, bold, prominent)
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  pdf.text(label.price, contentX + contentWidth / 2, contentY + contentHeight - 0.5, { align: 'center' });
};

/**
 * Generate PDF based on format type
 * @param {Object} item - Item data
 * @param {string} format - 'swing-tags' or 'avery-labels'
 * @param {Object} options - Additional options (startRow, startCol for Avery labels)
 */
export const generatePDF = async (item, format = 'swing-tags', options = {}) => {
  if (format === 'avery-labels') {
    const { startRow = 1, startCol = 1 } = options;
    return await generateAveryLabelsPDF(item, startRow, startCol);
  } else {
    return await generateSwingTagsPDF(item);
  }
};

/**
 * Generate a preview of the PDF (returns data URL)
 */
export const generatePDFPreview = (item, format = 'swing-tags') => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  const variations = item.variations || [];

  if (!variations.length) {
    return null;
  }

  if (format === 'avery-labels') {
    // Show full Avery label layout with first 12 variations
    const labelWidth = 44.45;
    const labelHeight = 12.7;
    const labelsPerRow = 4;
    const marginLeft = 7.62;    // Avery 5167 left margin (0.3")
    const marginTop = 13.1;     // Avery 5167 top margin
    const gapX = 7.62;          // Avery 5167 horizontal gutter (0.3")
    const gapY = 0;

    // Show first 12 labels to preview the layout
    const previewCount = Math.min(12, variations.length);

    for (let i = 0; i < previewCount; i++) {
      const variation = variations[i % variations.length];
      const row = Math.floor(i / labelsPerRow);
      const col = i % labelsPerRow;

      const x = marginLeft + col * (labelWidth + gapX);
      const y = marginTop + row * (labelHeight + gapY);

      const label = {
        name: item.name,
        size: variation.size,
        price: formatMoney(variation.price),
        sku: variation.sku
      };

      drawAveryLabel(pdf, x, y, labelWidth, labelHeight, label);
    }
  } else {
    // Preview swing tags - show first 4 tags
    const tagWidth = 98;
    const tagHeight = 40;
    const tagsPerRow = 2;
    const marginLeft = 9.95;
    const marginTop = 10;
    const gapY = 5;

    const previewCount = Math.min(4, variations.length);

    for (let i = 0; i < previewCount; i++) {
      const variation = variations[i % variations.length];
      const row = Math.floor(i / tagsPerRow);
      const col = i % tagsPerRow;

      const x = marginLeft + col * tagWidth;
      const y = marginTop + row * (tagHeight + gapY);

      const tag = {
        name: item.name,
        brand: item.brand || '',
        color: variation.color,
        size: variation.size,
        price: formatMoney(variation.price),
        sku: variation.sku,
        date: new Date().toLocaleDateString()
      };

      drawSwingTag(pdf, x, y, tagWidth, tagHeight, tag);
    }
  }

  return pdf.output('dataurlstring');
};

/**
 * Generate receipt PDF for transactions
 */
export const generateReceiptPDF = (transaction, cashTendered = null) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [80, 200] // Thermal receipt size (80mm width)
  });

  let yPosition = 10;

  // Store header
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('MRich', 40, yPosition, { align: 'center' });
  yPosition += 8;

  // Store info
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Thank you for your purchase', 40, yPosition, { align: 'center' });
  yPosition += 5;

  // Transaction date
  const date = new Date(transaction.timestamp);
  pdf.text(date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }), 40, yPosition, { align: 'center' });
  yPosition += 8;

  // Separator
  pdf.setLineWidth(0.3);
  pdf.line(5, yPosition, 75, yPosition);
  yPosition += 5;

  // Items header
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Item', 5, yPosition);
  pdf.text('Qty', 55, yPosition);
  pdf.text('Price', 75, yPosition, { align: 'right' });
  yPosition += 5;

  // Separator
  pdf.line(5, yPosition, 75, yPosition);
  yPosition += 4;

  // Items
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);

  transaction.items.forEach(item => {
    // Item name
    const itemName = item.name || 'Item';
    pdf.text(itemName.substring(0, 25), 5, yPosition);
    yPosition += 3;

    // Color and size (if available)
    if (item.color && item.size) {
      pdf.setFontSize(7);
      pdf.text(`${item.color} - ${item.size}`, 5, yPosition);
      yPosition += 3;
    }

    // SKU
    if (item.sku) {
      pdf.setFontSize(7);
      pdf.text(`SKU: ${item.sku}`, 5, yPosition);
    }

    // Quantity and price
    pdf.setFontSize(8);
    pdf.text(`${item.quantity}`, 55, yPosition);
    pdf.text(`$${(item.price * item.quantity).toFixed(2)}`, 75, yPosition, { align: 'right' });
    yPosition += 5;
  });

  // Separator
  yPosition += 2;
  pdf.line(5, yPosition, 75, yPosition);
  yPosition += 5;

  // Subtotal
  pdf.setFontSize(9);
  pdf.text('Subtotal:', 5, yPosition);
  pdf.text(`$${transaction.total.toFixed(2)}`, 75, yPosition, { align: 'right' });
  yPosition += 5;

  // Total
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text('Total:', 5, yPosition);
  pdf.text(`$${transaction.total.toFixed(2)}`, 75, yPosition, { align: 'right' });
  yPosition += 7;

  // Payment details (if cash tendered provided)
  if (cashTendered !== null) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);

    pdf.text('Cash Tendered:', 5, yPosition);
    pdf.text(`$${cashTendered.toFixed(2)}`, 75, yPosition, { align: 'right' });
    yPosition += 5;

    const change = cashTendered - transaction.total;
    pdf.text('Change:', 5, yPosition);
    pdf.text(`$${change.toFixed(2)}`, 75, yPosition, { align: 'right' });
    yPosition += 7;
  }

  // Separator
  pdf.line(5, yPosition, 75, yPosition);
  yPosition += 5;

  // Transaction ID
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Transaction ID:`, 5, yPosition);
  yPosition += 3;
  pdf.text(transaction.id.substring(0, 20), 5, yPosition);
  yPosition += 5;

  // Payment method
  pdf.text(`Payment: ${transaction.paymentMethod}`, 5, yPosition);
  yPosition += 5;

  // Status
  if (transaction.status === 'CANCELED') {
    yPosition += 3;
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('*** VOID ***', 40, yPosition, { align: 'center' });
  }

  // Footer
  yPosition += 10;
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Visit us at mrich.ca', 40, yPosition, { align: 'center' });

  // Download PDF
  const filename = `receipt_${transaction.id.substring(0, 8)}_${Date.now()}.pdf`;
  pdf.save(filename);
};
